import { Send, Server } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Checkbox } from '../../components/ui/checkbox'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Switch } from '../../components/ui/switch'
import { useMailConfig } from '../../hooks/useMailConfig'
import { MailConfigRequest } from '../../services/MailConfig.service'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type MailConfigFormFields = Omit<MailConfigRequest, 'authPass'>

const initialForm: MailConfigFormFields = {
  host: '',
  port: 587,
  secure: false,
  authUser: '',
  fromAddress: '',
}

const MailServer = () => {
  const {
    status,
    isLoadingStatus,
    isSaving,
    isSwitching,
    isSendingTest,
    loadStatus,
    save,
    setEnabled,
    sendTestEmail,
  } = useMailConfig()
  const [form, setForm] = useState<MailConfigFormFields>(initialForm)
  const [authPass, setAuthPass] = useState('')
  const [authPassConfirm, setAuthPassConfirm] = useState('')
  const [wantsPasswordChange, setWantsPasswordChange] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [testRecipient, setTestRecipient] = useState('')
  const [testRecipientError, setTestRecipientError] = useState<string | null>(null)
  const [hasPrefilled, setHasPrefilled] = useState(false)

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  // Prefill the form from the saved config once, so existing settings are visible and
  // editable instead of always starting blank. authPass is never returned by the API and
  // is handled separately (see wantsPasswordChange) - never clobber later in-progress edits.
  useEffect(() => {
    if (hasPrefilled || !status?.host) return

    setForm((prev) => ({
      ...prev,
      host: status.host ?? prev.host,
      port: status.port ?? prev.port,
      secure: status.secure ?? prev.secure,
      authUser: status.authUser ?? prev.authUser,
      fromAddress: status.fromAddress ?? prev.fromAddress,
    }))
    setHasPrefilled(true)
  }, [status, hasPrefilled])

  const isConfigured = status?.configured ?? false
  const isActive = status?.mailProvider === 'smtp'
  // Password fields are always required for first-time setup (nothing to keep); once
  // configured, they stay collapsed behind "Passwort ändern" until the user opts in.
  const passwordFieldsVisible = !isConfigured || wantsPasswordChange

  const clearValidationError = (field: string) => {
    setValidationErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const setField = <K extends keyof MailConfigFormFields>(
    field: K,
    value: MailConfigFormFields[K]
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    clearValidationError(field)
  }

  const validate = (): boolean => {
    const errors: Record<string, string> = {}

    if (!form.host.trim()) errors.host = 'Host wird benötigt'
    if (!Number.isInteger(form.port) || form.port < 1 || form.port > 65535) {
      errors.port = 'Port muss zwischen 1 und 65535 liegen'
    }
    if (!form.authUser.trim()) errors.authUser = 'Benutzername wird benötigt'
    if (passwordFieldsVisible) {
      if (!authPass.trim()) {
        errors.authPass = 'Passwort wird benötigt'
      } else if (authPass !== authPassConfirm) {
        errors.authPassConfirm = 'Passwörter stimmen nicht überein'
      }
    }
    if (!EMAIL_REGEX.test(form.fromAddress)) {
      errors.fromAddress = 'Bitte eine gültige E-Mail-Adresse angeben'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleCancelPasswordChange = () => {
    setWantsPasswordChange(false)
    setAuthPass('')
    setAuthPassConfirm('')
    clearValidationError('authPass')
    clearValidationError('authPassConfirm')
  }

  const handleSave = async () => {
    if (!validate()) return

    const payload: MailConfigRequest = {
      ...form,
      ...(passwordFieldsVisible ? { authPass } : {}),
    }

    try {
      await save(payload)
      setAuthPass('')
      setAuthPassConfirm('')
      setWantsPasswordChange(false)
    } catch {
      // useMailConfig's onError already surfaces a toast; nothing further to do here.
    }
  }

  const handleToggle = async (checked: boolean) => {
    try {
      await setEnabled(checked)
    } catch {
      // useMailConfig's onError already surfaces a toast; nothing further to do here.
    }
  }

  const handleSendTest = async () => {
    if (!EMAIL_REGEX.test(testRecipient)) {
      setTestRecipientError('Bitte eine gültige E-Mail-Adresse angeben')
      return
    }
    setTestRecipientError(null)

    try {
      await sendTestEmail(testRecipient)
    } catch {
      // useMailConfig's onError already surfaces a toast; nothing further to do here.
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Mail-Server</h2>
        <p className="text-muted-foreground text-sm">
          Versenden Sie Buchungs-E-Mails über Ihren eigenen SMTP-Server statt über den
          Standard-Absender.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Eigenen Mail-Server verwenden</CardTitle>
          <CardDescription>
            {isConfigured
              ? 'Aktivieren Sie Ihren gespeicherten SMTP-Server, um Buchungs-E-Mails darüber zu versenden.'
              : 'Speichern Sie zuerst Ihre SMTP-Einstellungen, bevor Sie diese aktivieren können.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="mail-provider-switch">
                {isActive ? 'Aktiv' : 'Deaktiviert (Standard-Absender wird verwendet)'}
              </Label>
            </div>
            <Switch
              id="mail-provider-switch"
              checked={isActive}
              disabled={!isConfigured || isSwitching || isLoadingStatus}
              onCheckedChange={handleToggle}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            SMTP-Einstellungen
          </CardTitle>
          <CardDescription>
            Die Verbindung wird beim Speichern geprüft, bevor die Einstellungen übernommen werden.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="host">Host</Label>
            <Input
              id="host"
              placeholder="smtp.beispiel.de"
              value={form.host}
              onChange={(e) => setField('host', e.target.value)}
              className={validationErrors.host ? 'border-red-500' : ''}
            />
            {validationErrors.host && (
              <p className="text-sm text-red-500">{validationErrors.host}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="port">Port</Label>
            <Input
              id="port"
              type="number"
              value={form.port}
              onChange={(e) => setField('port', Number(e.target.value))}
              className={validationErrors.port ? 'border-red-500' : ''}
            />
            {validationErrors.port && (
              <p className="text-sm text-red-500">{validationErrors.port}</p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="secure"
              checked={form.secure}
              onCheckedChange={(checked) => setField('secure', checked === true)}
            />
            <Label htmlFor="secure" className="cursor-pointer text-sm font-normal">
              TLS-Verschlüsselung verwenden
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="authUser">Benutzername</Label>
            <Input
              id="authUser"
              value={form.authUser}
              onChange={(e) => setField('authUser', e.target.value)}
              className={validationErrors.authUser ? 'border-red-500' : ''}
            />
            {validationErrors.authUser && (
              <p className="text-sm text-red-500">{validationErrors.authUser}</p>
            )}
          </div>

          {passwordFieldsVisible ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="authPass">
                  {isConfigured ? 'Neues Passwort' : 'Passwort'}
                </Label>
                <Input
                  id="authPass"
                  type="password"
                  value={authPass}
                  onChange={(e) => {
                    setAuthPass(e.target.value)
                    clearValidationError('authPass')
                  }}
                  className={validationErrors.authPass ? 'border-red-500' : ''}
                />
                {validationErrors.authPass && (
                  <p className="text-sm text-red-500">{validationErrors.authPass}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="authPassConfirm">
                  {isConfigured ? 'Neues Passwort bestätigen' : 'Passwort bestätigen'}
                </Label>
                <Input
                  id="authPassConfirm"
                  type="password"
                  value={authPassConfirm}
                  onChange={(e) => {
                    setAuthPassConfirm(e.target.value)
                    clearValidationError('authPassConfirm')
                  }}
                  className={validationErrors.authPassConfirm ? 'border-red-500' : ''}
                />
                {validationErrors.authPassConfirm && (
                  <p className="text-sm text-red-500">{validationErrors.authPassConfirm}</p>
                )}
              </div>

              {isConfigured && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCancelPasswordChange}
                >
                  Passwortänderung abbrechen
                </Button>
              )}
            </>
          ) : (
            <div className="space-y-2">
              <Label>Passwort</Label>
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <span className="text-muted-foreground text-sm">
                  Passwort ist gesetzt und wird aus Sicherheitsgründen nie angezeigt.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setWantsPasswordChange(true)}
                >
                  Passwort ändern
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="fromAddress">Absenderadresse</Label>
            <Input
              id="fromAddress"
              type="email"
              placeholder="reservierung@beispiel.de"
              value={form.fromAddress}
              onChange={(e) => setField('fromAddress', e.target.value)}
              className={validationErrors.fromAddress ? 'border-red-500' : ''}
            />
            {validationErrors.fromAddress && (
              <p className="text-sm text-red-500">{validationErrors.fromAddress}</p>
            )}
          </div>

          <Button onClick={handleSave} disabled={isSaving} className="w-full">
            {isSaving ? 'Wird geprüft und gespeichert...' : 'Speichern'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Verbindung testen
          </CardTitle>
          <CardDescription>
            {isConfigured
              ? 'Senden Sie eine Test-E-Mail über Ihren gespeicherten Mail-Server, unabhängig davon, ob er gerade aktiv ist.'
              : 'Speichern Sie zuerst Ihre SMTP-Einstellungen, um eine Test-E-Mail zu senden.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="testRecipient">Empfänger</Label>
            <Input
              id="testRecipient"
              type="email"
              placeholder="ihre@email.de"
              value={testRecipient}
              onChange={(e) => {
                setTestRecipient(e.target.value)
                setTestRecipientError(null)
              }}
              disabled={!isConfigured}
              className={testRecipientError ? 'border-red-500' : ''}
            />
            {testRecipientError && <p className="text-sm text-red-500">{testRecipientError}</p>}
          </div>

          <Button
            onClick={handleSendTest}
            disabled={!isConfigured || isSendingTest}
            variant="outline"
            className="w-full"
          >
            {isSendingTest ? 'Wird gesendet...' : 'Test-E-Mail senden'}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

export default MailServer
